import React from 'react';
import { Link } from 'react-router-dom';
import {
  adminProspectCampaignDetailPath,
  adminProspectProjectionStatusLabel,
  adminProspectResponseIntentLabel,
  adminProspectSourceTypeLabel,
  adminProspectVerificationLabel,
  formatAdminDate,
} from './adminProspectDisplay';

export function ProspectOwnerResponseField({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

export function ProspectOwnerResponseCard({
  title,
  description,
  fields,
  children,
  hideTitle = false,
  embedded = false,
}) {
  // Always <section> — never switch host tags across the mobile breakpoint.
  const chrome = embedded
    ? 'min-w-0'
    : 'rounded-xl border border-slate-200 bg-white p-4';
  return (
    <section className={chrome} data-embedded={embedded ? 'true' : 'false'}>
      {!hideTitle && title ? <h2 className="text-sm font-semibold text-slate-800">{title}</h2> : null}
      {description ? (
        <p className={`${hideTitle ? 'mt-0' : 'mt-2'} text-sm text-slate-600`}>{description}</p>
      ) : null}
      {fields ? (
        <dl className={`${hideTitle && !description ? 'mt-0' : 'mt-3'} grid gap-3 sm:grid-cols-2`}>{fields}</dl>
      ) : null}
      {children ? (
        <div className={fields || description || !hideTitle ? 'mt-3 space-y-3' : 'space-y-3'}>{children}</div>
      ) : null}
    </section>
  );
}

export function ProspectCampaignLink({ campaignId, t, children }) {
  const path = adminProspectCampaignDetailPath(campaignId);
  if (!path) return null;
  return (
    <Link
      to={path}
      className="font-semibold text-indigo-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {children || t('admin.prospection.primaryContact.openCampaign')}
    </Link>
  );
}

export function ProspectProjectionProvenance({ provenance, language, t }) {
  if (!provenance) return null;
  const intent = adminProspectResponseIntentLabel(provenance.responseIntent, t);
  const source = adminProspectSourceTypeLabel(provenance.sourceType, t);
  const submitted = formatAdminDate(provenance.submittedAt, language);
  return (
    <>
      {intent ? (
        <ProspectOwnerResponseField label={t('admin.prospection.primaryContact.intent')}>
          {intent}
        </ProspectOwnerResponseField>
      ) : null}
      {source ? (
        <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.source')}>
          {source}
        </ProspectOwnerResponseField>
      ) : null}
      {submitted && submitted !== '—' ? (
        <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.submittedAt')}>
          {submitted}
        </ProspectOwnerResponseField>
      ) : null}
      {adminProspectCampaignDetailPath(provenance.campaignId) ? (
        <ProspectOwnerResponseField label={t('admin.prospection.primaryContact.campaign')}>
          <ProspectCampaignLink campaignId={provenance.campaignId} t={t} />
        </ProspectOwnerResponseField>
      ) : null}
    </>
  );
}

export function ProspectProjectionVerification({
  section,
  verificationState,
  busy,
  pending,
  error,
  t,
  onRequestChange,
  onConfirm,
  onCancel,
  onReload,
}) {
  const verified = verificationState === 'admin_verified';
  const confirming = pending?.section === section;
  const confirmTarget = confirming ? pending.verificationState : null;
  return (
    <div className="border-t border-slate-100 pt-3">
      <p className="text-sm text-slate-800">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
            verified ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-amber-50 text-amber-950 ring-amber-200'
          }`}
        >
          {adminProspectVerificationLabel(verificationState, t)}
        </span>
      </p>
      {confirming ? (
        <div className="mt-3 space-y-2" role="group" aria-label={t('admin.prospection.ownerResponse.verification.confirm')}>
          <p className="text-sm text-slate-700">
            {confirmTarget === 'unverified'
              ? t('admin.prospection.ownerResponse.verification.confirmUnmark')
              : t('admin.prospection.ownerResponse.verification.confirmMark')}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onConfirm}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {busy
                ? t('admin.prospection.ownerResponse.verification.saving')
                : t('admin.prospection.ownerResponse.verification.confirm')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.ownerResponse.verification.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => onRequestChange({ section })}
          className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          {verified
            ? t('admin.prospection.ownerResponse.verification.unmark')
            : t('admin.prospection.ownerResponse.verification.mark')}
        </button>
      )}
      {error ? (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
          <p>{error.message}</p>
          {error.stale ? (
            <button
              type="button"
              onClick={onReload}
              className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {t('admin.prospection.ownerResponse.verification.reload')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ProspectPublicResponseActivity({ item, language, t }) {
  const intent = adminProspectResponseIntentLabel(item.intent, t);
  const submitted = formatAdminDate(item.submittedAt || item.createdAt, language);
  const status = adminProspectProjectionStatusLabel(item.projectionStatus, t);
  return (
    <div className="mt-1 space-y-1 text-sm text-slate-800">
      <p>{t('admin.prospection.activity.publicResponse.detail')}</p>
      {intent ? (
        <p>
          {t('admin.prospection.primaryContact.intent')}: {intent}
        </p>
      ) : null}
      {submitted && submitted !== '—' ? (
        <p>
          {t('admin.prospection.activity.publicResponse.submittedAt')}: {submitted}
        </p>
      ) : null}
      <ul className="flex flex-wrap gap-2 pt-1">
        <li className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200">
          {item.projectedContact
            ? t('admin.prospection.activity.publicResponse.projectedContact')
            : t('admin.prospection.activity.publicResponse.notProjectedContact')}
        </li>
        <li className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200">
          {item.projectedPropertyProfile
            ? t('admin.prospection.activity.publicResponse.projectedBuilding')
            : t('admin.prospection.activity.publicResponse.notProjectedBuilding')}
        </li>
        <li className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200">
          {item.projectedFinancialSnapshot
            ? t('admin.prospection.activity.publicResponse.projectedFinancial')
            : t('admin.prospection.activity.publicResponse.notProjectedFinancial')}
        </li>
      </ul>
      <p>
        {t('admin.prospection.activity.publicResponse.projectionStatus')}: {status}
      </p>
      {adminProspectCampaignDetailPath(item.campaignId) ? (
        <p>
          {t('admin.prospection.activity.publicResponse.campaign')}:{' '}
          <ProspectCampaignLink campaignId={item.campaignId} t={t} />
        </p>
      ) : null}
    </div>
  );
}
